import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import GoBack from "components/actions/GoBack";
import Card from "components/card";
import CreateButton from "components/buttons/CreateButton";
import { importCustomers } from "interfaces/customer";
import { PROSPECT_FIELDS, guessFieldForHeader } from "./fieldDefinitions";

type Step = "upload" | "mapping" | "result";

type ImportError = { index: number; company: string; message: string };

const PREVIEW_ROW_COUNT = 5;

const buildTagsValue = (raw: string): string[] =>
    raw
        .split(/[,;]/)
        .map((t) => t.trim())
        .filter(Boolean);

const ImportProspect = () => {
    const nav = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [step, setStep] = useState<Step>("upload");
    const [fileName, setFileName] = useState("");
    const [headers, setHeaders] = useState<string[]>([]);
    const [rawRows, setRawRows] = useState<Record<string, any>[]>([]);
    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [isDragging, setIsDragging] = useState(false);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState<{ created: number; errors: ImportError[]; skipped: number } | null>(null);

    const resetToUpload = () => {
        setStep("upload");
        setFileName("");
        setHeaders([]);
        setRawRows([]);
        setMapping({});
        setResult(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleFile = async (file: File) => {
        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
            const firstSheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[firstSheetName];
            const rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, {
                defval: "",
                raw: false,
            });

            if (!rows.length) {
                toast.error("Aucune ligne de données trouvée dans le fichier.");
                return;
            }

            const detectedHeaders = Object.keys(rows[0]);
            const initialMapping: Record<string, string> = {};
            detectedHeaders.forEach((h) => {
                initialMapping[h] = guessFieldForHeader(h);
            });

            setFileName(file.name);
            setHeaders(detectedHeaders);
            setRawRows(rows);
            setMapping(initialMapping);
            setStep("mapping");
        } catch (err) {
            toast.error("Impossible de lire ce fichier. Vérifiez qu'il s'agit bien d'un CSV ou XLSX valide.");
        }
    };

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const companyIsMapped = Object.values(mapping).includes("company");

    const buildProspects = () => {
        const mappedFields = Object.entries(mapping).filter(([, field]) => field);

        return rawRows.map((row) => {
            const obj: Record<string, any> = { status: "prospect" };
            mappedFields.forEach(([header, field]) => {
                const raw = row[header];
                if (raw === undefined || raw === null) return;
                const value = String(raw).trim();
                if (!value) return;

                if (field === "tags") {
                    obj.tags = buildTagsValue(value);
                } else if (field === "potentialMonthlyRevenue") {
                    const num = Number(value.replace(",", "."));
                    if (!Number.isNaN(num)) obj.potentialMonthlyRevenue = num;
                } else {
                    obj[field] = value;
                }
            });
            return obj;
        });
    };

    const handleImport = async () => {
        const allProspects = buildProspects();
        const prospects = allProspects.filter((p) => !!p.company);
        const skipped = allProspects.length - prospects.length;

        if (!prospects.length) {
            toast.error("Aucun prospect valide à importer (raison sociale manquante sur toutes les lignes).");
            return;
        }

        setImporting(true);
        const res = await importCustomers(prospects);
        setImporting(false);

        if (res) {
            setResult({
                created: res.created ?? prospects.length,
                errors: res.errors ?? [],
                skipped,
            });
            setStep("result");
        }
    };

    const previewRows = rawRows.slice(0, PREVIEW_ROW_COUNT);
    const mappedHeadersForPreview = headers.filter((h) => mapping[h]);

    return (
        <>
            <div className="mb-4">
                <GoBack text="Mes opportunités" />
            </div>

            {step === "upload" && (
                <Card extra="w-full !p-6 border border-[#7090B0]/[0.25] rounded-xl">
                    <h4 className="text-lg font-medium text-navy-700 dark:text-white">
                        Importer des prospects
                    </h4>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                        Déposez un fichier CSV ou Excel (.xlsx, .xls). La première ligne du fichier doit contenir les en-têtes de colonnes.
                    </p>

                    <div
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={onDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`mt-5 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-14 text-center transition ${
                            isDragging
                                ? "border-[#0260CB] bg-[#0260CB]/5"
                                : "border-gray-300 dark:border-navy-600"
                        }`}
                    >
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            Glissez-déposez votre fichier ici, ou cliquez pour le sélectionner
                        </p>
                        <p className="mt-1 text-xs text-gray-400">.csv, .xlsx, .xls</p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={onFileInputChange}
                            className="hidden"
                        />
                    </div>
                </Card>
            )}

            {step === "mapping" && (
                <Card extra="w-full !p-6 border border-[#7090B0]/[0.25] rounded-xl">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <h4 className="text-lg font-medium text-navy-700 dark:text-white">
                                Associez vos colonnes
                            </h4>
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                {fileName} — {rawRows.length} ligne{rawRows.length > 1 ? "s" : ""} détectée{rawRows.length > 1 ? "s" : ""}
                            </p>
                        </div>
                        <button
                            className="text-sm text-[#0260CB] underline"
                            onClick={resetToUpload}
                        >
                            Changer de fichier
                        </button>
                    </div>

                    {!companyIsMapped && (
                        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                            Vous devez associer une colonne à la "Raison sociale" pour pouvoir importer.
                        </div>
                    )}

                    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {headers.map((header) => (
                            <div key={header} className="flex flex-col">
                                <label className="text-xs font-medium text-navy-700 dark:text-white truncate" title={header}>
                                    {header}
                                </label>
                                <select
                                    value={mapping[header] || ""}
                                    onChange={(e) =>
                                        setMapping((prev) => ({ ...prev, [header]: e.target.value }))
                                    }
                                    className="mt-1 h-9 rounded-md border border-gray-300 px-2 text-sm outline-none focus:border-[#0260CB]"
                                >
                                    <option value="">Ignorer cette colonne</option>
                                    {PROSPECT_FIELDS.map((field) => (
                                        <option key={field.key} value={field.key}>
                                            {field.label}
                                            {field.required ? " *" : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                            Aperçu ({previewRows.length} premières lignes)
                        </p>
                        <div className="mt-2 overflow-x-auto rounded-md border border-gray-200 dark:border-navy-700">
                            <table className="min-w-full text-left text-xs">
                                <thead className="bg-gray-50 dark:bg-navy-900">
                                    <tr>
                                        {mappedHeadersForPreview.map((header) => (
                                            <th key={header} className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">
                                                {PROSPECT_FIELDS.find((f) => f.key === mapping[header])?.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewRows.map((row, i) => (
                                        <tr key={i} className="border-t border-gray-100 dark:border-navy-700">
                                            {mappedHeadersForPreview.map((header) => (
                                                <td key={header} className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                                    {String(row[header] ?? "")}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="mt-6">
                        <CreateButton
                            value={importing ? "Import en cours..." : `Importer ${rawRows.length} prospect${rawRows.length > 1 ? "s" : ""}`}
                            onClick={handleImport}
                            extra={!companyIsMapped || importing ? "opacity-50 pointer-events-none" : ""}
                        />
                    </div>
                </Card>
            )}

            {step === "result" && result && (
                <Card extra="w-full !p-6 border border-[#7090B0]/[0.25] rounded-xl">
                    <h4 className="text-lg font-medium text-navy-700 dark:text-white">
                        Import terminé
                    </h4>
                    <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                        {result.created} prospect{result.created > 1 ? "s" : ""} importé{result.created > 1 ? "s" : ""} avec succès.
                    </p>
                    {result.skipped > 0 && (
                        <p className="mt-1 text-sm text-amber-600">
                            {result.skipped} ligne{result.skipped > 1 ? "s" : ""} ignorée{result.skipped > 1 ? "s" : ""} (raison sociale manquante).
                        </p>
                    )}
                    {result.errors.length > 0 && (
                        <div className="mt-3">
                            <p className="text-sm font-medium text-red-600">
                                {result.errors.length} ligne{result.errors.length > 1 ? "s" : ""} en erreur :
                            </p>
                            <ul className="mt-1 max-h-48 overflow-y-auto text-xs text-red-600 list-disc pl-5">
                                {result.errors.map((err) => (
                                    <li key={err.index}>
                                        {err.company || `Ligne ${err.index + 1}`} — {err.message}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className="mt-6">
                        <CreateButton value="Aller à mes opportunités" onClick={() => nav("/kanban")} />
                    </div>
                </Card>
            )}
        </>
    );
};

export default ImportProspect;
